import React, { useRef, useMemo, ReactElement, Ref, useEffect } from 'react';
import classNames from 'classnames';
import { CSSTransition } from 'react-transition-group';
import useCallbackRef from 'hooks/useCallbackRef';
import useCSSVariables from 'hooks/useCSSVariables';
import useAnimation, {
  AnimationProps as SimpleAnimationProps,
  EnhancedAnimationProps,
} from './hooks/useAnimation';

import './Animation.styles.scss';

export const EASE_IN_OUT_CUBIC = 'cubic-bezier(0.645, 0.045, 0.355, 1)';

export type AnimationProps = {
  className?: string;
  children?: React.ReactNode;
  open: boolean;
  nodeRef?: Ref<HTMLElement | undefined>;
  unmountOnExit?: boolean;
} & (EnhancedAnimationProps | SimpleAnimationProps);

const Animation: React.FC<AnimationProps> = (props) => {
  const {
    className,
    children,
    open,
    unmountOnExit = true,
    ...animationRest
  } = props;

  const { duration = 600 } = animationRest;
  const [container, containerRef] = useCallbackRef<HTMLElement>();

  const {
    appearAnimations = {},
    disappearAnimations = {},
    appearAnimationText,
    disappearAnimationText,
  } = useAnimation({
    duration,
    ...animationRest,
  });

  const styles = useCSSVariables({
    target: container,
    stylesObject: {
      'ncoded-appear-animation': appearAnimationText,
      'ncoded-disappear-animation': disappearAnimationText,
    },
  });

  const classes = classNames('ncoded-animation', {
    'ncoded-animation--appear':
      Object.keys(appearAnimations).includes('appear') ||
      Object.keys(disappearAnimations).includes('appear'),
    'ncoded-animation--move-up':
      Object.keys(appearAnimations).includes('moveUp') ||
      Object.keys(disappearAnimations).includes('moveUp'),
    'ncoded-animation--move-down':
      Object.keys(appearAnimations).includes('moveDown') ||
      Object.keys(disappearAnimations).includes('moveDown'),
    'ncoded-animation--move-left':
      Object.keys(appearAnimations).includes('moveLeft') ||
      Object.keys(disappearAnimations).includes('moveLeft'),
    'ncoded-animation--move-right':
      Object.keys(appearAnimations).includes('moveRight') ||
      Object.keys(disappearAnimations).includes('moveRight'),
  });

  const extendedChildren = useMemo(() => {
    const rootContainer = (el: any) => {
      if (!el) return;

      if (typeof el !== 'object') {
        containerRef(el as HTMLElement);
        return;
      }
      if (typeof el === 'object' && el?.container) {
        containerRef(el.container as HTMLElement);
        return;
      }

      if ((el as HTMLElement) instanceof HTMLElement) {
        containerRef(el as HTMLElement);
      }
    };

    return React.cloneElement(children as ReactElement, {
      ref: rootContainer,
      className: `${(children as ReactElement).props?.className} ${classes}`,
      children: (
        <>
          {styles}
          {(children as ReactElement).props.children}
        </>
      ),
    });
  }, [children, classes, containerRef, styles]);

  const nodeRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!container) return;

    nodeRef.current = container as HTMLDivElement;
  }, [container]);

  return (
    <CSSTransition
      nodeRef={nodeRef}
      classNames={{
        enter: 'ncoded-animation-enter',
        enterActive: 'ncoded-animation-enter-active',
        enterDone: 'ncoded-animation-enter-done',
        exit: 'ncoded-animation-ext',
        exitActive: 'ncoded-animation-exit-active',
        exitDone: 'ncoded-animation-exit-done',
      }}
      timeout={duration}
      unmountOnExit={unmountOnExit}
      exit={true}
      in={open}
    >
      {extendedChildren}
    </CSSTransition>
  );
};

export default Animation;
